import React from 'react';
import { Box, Typography } from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import CompactAlertCard from '../cards/CompactAlertCard';

/** VA detection type ids */
export const VA_TYPE_IDS = ['cd', 'vd', 'pd', 'id', 'vc', 'ls', 'ct', 'sh', 'ii'];

/** VTS detection type ids */
export const VTS_TYPE_IDS = ['or', 'oa', 'ta', 'usp', 'os'];

const SectionHeader = ({ icon: Icon, label, accentColor, totalEvents }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.2,
      mb: 1.5,
      pb: 0.8,
      borderBottom: `2px solid ${accentColor}33`,
      flexShrink: 0,
    }}
  >
    <Box sx={{ width: 4, height: 20, borderRadius: 2, backgroundColor: accentColor, flexShrink: 0 }} />
    <Icon sx={{ color: accentColor, fontSize: '1rem' }} />
    <Typography
      sx={{
        color: accentColor,
        fontWeight: 700,
        fontSize: '0.78rem',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        userSelect: 'none',
      }}
    >
      {label}
    </Typography>

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, ml: 'auto' }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          backgroundColor: totalEvents > 0 ? '#E74C3C' : '#3d5166',
          boxShadow: totalEvents > 0 ? '0 0 6px #E74C3C' : 'none',
          animation: totalEvents > 0 ? 'liveDot 1.4s ease-in-out infinite' : 'none',
          '@keyframes liveDot': {
            '0%,100%': { opacity: 1 },
            '50%': { opacity: 0.3 },
          },
        }}
      />
      <Typography sx={{ color: totalEvents > 0 ? '#E74C3C' : '#3d5166', fontSize: '0.66rem', fontWeight: 600 }}>
        {totalEvents > 0 ? `${totalEvents} LIVE` : 'NO EVENTS'}
      </Typography>
    </Box>
  </Box>
);

/**
 * HomeDashboard
 *
 * Normal mode  → shows both VA and VTS sections stacked.
 * vtsOnlyMode  → shows only the VTS section (used below the event card
 *                in split view, since VA is in the right sidebar).
 */
const HomeDashboard = ({
  detectionTypes,
  getCount,
  selectedId,
  onSelect,
  vtsOnlyMode = false,
  boomCount = 0,
  onBoomClick,
}) => {
  const vaTypes  = detectionTypes.filter(t => VA_TYPE_IDS.includes(t.id));
  const vtsTypes = detectionTypes.filter(t => VTS_TYPE_IDS.includes(t.id));

  const vaTotalEvents  = vaTypes.reduce((s, t)  => s + getCount(t.id), 0);
  const vtsTotalEvents = vtsTypes.reduce((s, t) => s + getCount(t.id), 0) + boomCount;

  // Fluid grid: columns auto-fit to the available width (zoom/resize friendly)
  // and every cell is forced to the same height via gridAutoRows so all
  // cards are perfectly uniform regardless of how many there are.
  const gridSx = (minCol) => ({
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fit, minmax(${minCol}, 1fr))`,
    gridAutoRows: '1fr',
    gap: 1.5,
  });

  // VTS grid: detection cards + the boom-barrier card (when wired in)
  const renderVtsGrid = (minCol = '210px') => (
    <Box sx={gridSx(minCol)}>
      {vtsTypes.map(type => (
        <CompactAlertCard
          key={type.id}
          id={type.id}
          title={type.title}
          count={getCount(type.id)}
          selected={selectedId === type.id}
          onClick={() => onSelect(type.id)}
          fillHeight
        />
      ))}
      {onBoomClick && (
        <CompactAlertCard
          key="boom"
          id="boom"
          title="Open Boom Barriers"
          count={boomCount}
          selected={selectedId === 'boom'}
          onClick={onBoomClick}
          fillHeight
        />
      )}
    </Box>
  );

  const renderGrid = (types, minCol = '190px') => (
    <Box sx={gridSx(minCol)}>
      {types.map(type => (
        <CompactAlertCard
          key={type.id}
          id={type.id}
          title={type.title}
          count={getCount(type.id)}
          selected={selectedId === type.id}
          onClick={() => onSelect(type.id)}
          fillHeight
        />
      ))}
    </Box>
  );

  if (vtsOnlyMode) {
    return (
      <Box
        sx={{
          backgroundColor: 'rgba(20,32,44,0.75)',
          border: '1px solid rgba(230,126,34,0.18)',
          borderRadius: '12px',
          p: 2,
        }}
      >
        <SectionHeader
          icon={DirectionsBusIcon}
          label="GPS-RFID Alerts"
          accentColor="#E67E22"
          totalEvents={vtsTotalEvents}
        />
        {renderVtsGrid('200px')}
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* ── VIDEO ANALYTICS ALERT — 3 columns, 3 rows ── */}
      <Box sx={{ backgroundColor: 'rgba(20,32,44,0.75)', border: '1px solid rgba(26,188,156,0.18)', borderRadius: '12px', p: 2 }}>
        <SectionHeader icon={VideocamIcon} label="Video Analytics Alert" accentColor="#1ABC9C" totalEvents={vaTotalEvents} />
        {renderGrid(vaTypes, '190px')}
      </Box>

      {/* ── GPS-RFID ALERTS (vehicle tracking + boom barriers) ── */}
      <Box sx={{ backgroundColor: 'rgba(20,32,44,0.75)', border: '1px solid rgba(230,126,34,0.18)', borderRadius: '12px', p: 2 }}>
        <SectionHeader icon={DirectionsBusIcon} label="GPS-RFID Alerts" accentColor="#E67E22" totalEvents={vtsTotalEvents} />
        {renderVtsGrid('210px')}
      </Box>
    </Box>
  );
};

export default HomeDashboard;
